import { useState, useEffect, useCallback } from "react";
import { Button, Card, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow } from "@tremor/react";
import { useTranslation } from "react-i18next";
import Badge from "../Badge.jsx";
import { fmtMs } from "../../utils/format.js";
import { ChevronRight } from "lucide-react";
import { CheckDetailSummary } from "./PlaywrightCheckDrawer.jsx";
import { fetchMonitorChecks } from "../../api/monitors.js";

const STATUS_BADGE_COLOR = {
  up: "emerald",
  degraded: "orange",
  down: "red",
  timeout: "red",
  error: "red",
  needs_attention: "amber",
};

function isActionableConfigCheck(check) {
  return check?.data?.retryable === false;
}

const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "up", label: "Up" },
  { value: "degraded", label: "Degraded" },
  { value: "down", label: "Down / Error" },
];

export default function CheckRunsHistory({
  monitorId,
  monitor,
  locationLabels,
  locations,
  setExpandedCheck,
  timingColKey = "history.response_time",
}) {
  const { t } = useTranslation("monitor_detail");

  const [checkRuns, setCheckRuns] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedRunId, setExpandedRunId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");

  const load = useCallback(
    async (reset = true, cursor = null) => {
      if (reset) setLoading(true);
      try {
        const params = { limit: 30 };
        if (cursor) params.before = cursor;
        if (statusFilter !== "all") params.status = statusFilter;
        if (locationFilter !== "all") params.location = locationFilter;

        const res = await fetchMonitorChecks(monitorId, params);
        const rows = res.data ?? [];
        setCheckRuns((prev) => (reset ? rows : [...prev, ...rows]));
        setNextCursor(res.meta?.next_cursor ?? null);
      } catch {
        // silently ignore
      } finally {
        if (reset) setLoading(false);
      }
    },
    [monitorId, statusFilter, locationFilter]
  );

  useEffect(() => {
    load(true);
  }, [load]);

  function handleStatusFilter(value) {
    setStatusFilter(value);
    setExpandedRunId(null);
  }

  function handleLocationFilter(e) {
    setLocationFilter(e.target.value);
    setExpandedRunId(null);
  }

  return (
    <Card className="p-0">
      <div className="border-b border-gray-100 px-5 py-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-gray-700">{t("history.title")}</h2>

        <div className="flex flex-wrap items-center gap-2">
          {/* Status filter pills */}
          <div className="flex items-center gap-1">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => handleStatusFilter(f.value)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  statusFilter === f.value
                    ? "bg-teal-600 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Location filter */}
          {locations.length > 1 && (
            <select
              value={locationFilter}
              onChange={handleLocationFilter}
              className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-teal-500"
            >
              <option value="all">All locations</option>
              {locations.map((loc) => (
                <option key={loc} value={loc}>
                  {locationLabels[loc] ?? loc}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {loading ? (
        <p className="px-5 py-8 text-center text-sm text-gray-400">{t("loading")}</p>
      ) : checkRuns.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-gray-400">
          {statusFilter !== "all" || locationFilter !== "all"
            ? "No check runs match the current filters."
            : t("history.empty")}
        </p>
      ) : (
        <>
          <div className="divide-y divide-gray-50">
            {checkRuns.map((run) => {
              const isExpanded = expandedRunId === run.id;
              return (
                <div key={run.id}>
                  <div
                    className="flex items-center gap-4 px-5 py-3 hover:bg-gray-50 cursor-pointer select-none"
                    onClick={() => setExpandedRunId(isExpanded ? null : run.id)}
                  >
                    <span className="w-36 shrink-0 text-xs text-gray-500 whitespace-nowrap">
                      {new Date(run.scheduled_at).toLocaleString()}
                    </span>
                    <Badge color={STATUS_BADGE_COLOR[run.overall_status] ?? "gray"}>
                      {run.overall_status}
                    </Badge>
                    <div className="flex flex-wrap gap-2 flex-1">
                      {run.results.map((r) => (
                        <span
                          key={r.location}
                          className="inline-flex items-center gap-1.5 text-xs text-gray-600"
                        >
                          <span
                            className={`h-2 w-2 shrink-0 rounded-full ${
                              r.status === "up"
                                ? "bg-green-500"
                                : r.status === "degraded"
                                  ? "bg-amber-400"
                                  : "bg-red-500"
                            }`}
                          />
                          {locationLabels[r.location] ?? r.location ?? "—"}
                        </span>
                      ))}
                    </div>
                    <span className="text-gray-300 text-xs">{isExpanded ? "▲" : "▼"}</span>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-gray-100 bg-gray-50 px-5 py-3">
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableHeaderCell>{t("history.location")}</TableHeaderCell>
                              <TableHeaderCell>{t("history.status")}</TableHeaderCell>
                              <TableHeaderCell>{t(timingColKey)}</TableHeaderCell>
                              <TableHeaderCell>{t("history.details")}</TableHeaderCell>
                              <TableHeaderCell></TableHeaderCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {run.results.map((r) => {
                              const d = r.data ?? {};
                              const hasData = Object.keys(d).length > 0 || !!r.error;
                              return (
                                <TableRow key={r.location ?? "unknown"}>
                                  <TableCell className="text-xs text-gray-700 whitespace-nowrap font-medium">
                                    {locationLabels[r.location] ?? r.location ?? "—"}
                                  </TableCell>
                                  <TableCell>
                                    <Badge
                                      color={
                                        STATUS_BADGE_COLOR[
                                          isActionableConfigCheck(r) ? "needs_attention" : r.status
                                        ] ?? "gray"
                                      }
                                    >
                                      {isActionableConfigCheck(r)
                                        ? t("history.needs_attention")
                                        : r.status}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-gray-500">
                                    {fmtMs(r.response_time_ms)}
                                  </TableCell>
                                  <TableCell className="text-xs text-gray-500 max-w-xs">
                                    <CheckDetailSummary
                                      type={monitor.type}
                                      data={d}
                                      error={r.error}
                                    />
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {hasData && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setExpandedCheck({ ...r, time: run.scheduled_at });
                                        }}
                                        className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1 text-xs font-medium text-teal-700 hover:border-teal-300 hover:bg-teal-50"
                                        title={t("history.view_details")}
                                      >
                                        {t("history.view_details")}
                                        <ChevronRight className="h-3.5 w-3.5" />
                                      </button>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {nextCursor && (
            <div className="border-t border-gray-100 px-5 py-3 text-center">
              <Button
                variant="secondary"
                size="xs"
                onClick={() => load(false, nextCursor)}
              >
                {t("history.load_more")}
              </Button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
